import api from "../helpers/axios";
import fileRelationService from "./fileRelationService";

class CollectionsService {
  /**
   * Get collections with pagination and optional search
   * @param {number} page - Page number (1-based)
   * @param {number} limit - Items per page
   * @param {string|null} search - Search query
   * @returns {Promise<GetCollectionsResponse>} Collections and pagination data
   */
  async getCollections(page = 1, limit = 10, search = null, facilityId = null) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      // Backend requires a minimum of 2 characters (Query min_length=2) and
      // returns 422 otherwise, so only send `search` once it's long enough.
      // A 1-char (or empty) query is treated as "no search".
      if (search && search.trim().length >= 2) {
        params.append("search", search.trim());
      }

      if (facilityId != null && facilityId !== "") {
        params.append("facility_id", facilityId.toString());
      }

      const response = await api.get(`/collections/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching collections:", error);
      throw error;
    }
  }

  /**
   * Create a new collection
   * @param {string} name - Collection name
   * @param {string} description - Collection description
   * @returns {Promise<Collection>} Created collection data
   */
  async createCollection(
    name,
    description,
    facilityId = null,
    isOrgCollection = true,
    isAgentCollection = false
  ) {
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        files: [],
        // The admin portal creates organization collections. The backend only
        // honours these flags for a superadmin/admin caller; anyone else gets
        // false regardless of what we send.
        is_org_collection: Boolean(isOrgCollection),
        is_agent_collection: Boolean(isAgentCollection),
      };

      // Superadmins can create a collection for the facility selected in the
      // top facility filter. Backend ignores this for non-superadmins.
      if (facilityId != null && facilityId !== "") {
        payload.facility_id = Number(facilityId);
      }

      const response = await api.post("/collections/", payload);
      return response.data;
    } catch (error) {
      console.error("Error creating collection:", error);
      throw error;
    }
  }

  /**
   * Toggle the publication status of a collection (Admin only)
   * @param {string} collectionId - Collection ID
   * @param {boolean} isPublished - New publication status
   * @returns {Promise<Collection>} Updated collection data
   */
  async togglePublishStatus(collectionId, isPublished) {
    try {
      const payload = {
        is_published: isPublished,
      };

      const response = await api.patch(
        `/collections/admin/${collectionId}/toggle-publish`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error toggling publish status:", error);
      throw error;
    }
  }

  /**
   * Update a collection's name and description
   * @param {string} collectionId - Collection ID
   * @param {Object} updateData - Data to update { name, description }
   * @returns {Promise<Collection>} Updated collection data
   */
  async updateCollection(collectionId, updateData) {
    try {
      const payload = {
        name: updateData.name?.trim(),
        description: updateData.description?.trim(),
        workspace_id: null, // Keep existing workspace_id
        prompt_instructions: updateData.prompt_instructions || "", // Keep existing or empty
      };

      const response = await api.put(`/collections/${collectionId}`, payload);

      return response.data;
    } catch (error) {
      console.error("Error updating collection:", error);
      throw error;
    }
  }

  /**
   * Get collection with its associated files
   * @param {string} collectionId - Collection ID
   * @returns {Promise<Object>} Collection with files data
   */
  async getCollectionFiles(collectionId) {
    try {
      // Get file relations for this collection
      const fileRelations = await fileRelationService.getFileRelations(
        collectionId
      );

      // Extract files from file relations
      const files = fileRelations
        .map((relation) => relation.file)
        .filter((file) => file);

      return {
        id: collectionId,
        files: files,
      };
    } catch (error) {
      console.error("Error fetching collection files:", error);
      throw error;
    }
  }

  /**
   * Delete a collection (soft delete)
   * @param {string} collectionId - Collection ID
   * @returns {Promise<Object>} Delete response
   */
  async deleteCollection(collectionId) {
    try {
      const response = await api.delete(`/collections/${collectionId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting collection:", error);
      throw error;
    }
  }

  /**
   * Get detailed collection information by collection ID
   * @param {string} collectionId - Collection ID
   * @returns {Promise<Object>} Collection details
   */
  async getCollectionDetails(collectionId) {
    try {
      const response = await api.get(`/collections/${collectionId}/details`);
      return response.data;
    } catch (error) {
      console.error("Error fetching collection details:", error);
      throw error;
    }
  }

  /**
   * Format date from ISO string to DD/MM/YYYY
   * @param {string} isoString - ISO date string
   * @returns {string} Formatted date string
   */
  formatDate(isoString) {
    try {
      const date = new Date(isoString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  }
}

export default new CollectionsService();
